import { Link } from 'react-router-dom';
import { CalendarClock, CalendarHeart, LifeBuoy } from 'lucide-react';

import { addDaysISO, formatShort, type CyclePrediction } from '@/lib/cycle';

interface AdaptiveBannerProps {
  prediction: CyclePrediction | null;
  /** Gentle mode: predict period timing only, with no pain/flare-up language. */
  gentle?: boolean;
  /** Today's actual logged pain (0-10), if the user has logged today. */
  todayPain?: number;
}

/**
 * Prominent adaptive banner. Reacts first to actual high pain logged today,
 * then to predicted flare days — suggesting rescheduling and the SOS guide.
 * In gentle mode it simply highlights the upcoming period.
 */
export function AdaptiveBanner({ prediction, gentle = false, todayPain }: AdaptiveBannerProps) {
  /* ------------ Actual severe pain logged today (highest priority) ------------ */
  if (!gentle && typeof todayPain === 'number' && todayPain >= 7) {
    const lightStart = prediction ? addDaysISO(prediction.nextPeriodStart, 6) : null;
    const lightEnd = prediction ? addDaysISO(prediction.nextPeriodStart, 11) : null;

    return (
      <section
        aria-live="polite"
        className="rounded-3xl border border-blush-strong/40 bg-gradient-to-br from-blush via-white to-lavender p-5 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-blush-strong/15 text-blush-foreground">
            <LifeBuoy className="size-6" aria-hidden />
          </span>
          <div className="space-y-2">
            <h2 className="text-lg font-bold leading-snug text-blush-foreground">
              High pain logged today ({todayPain}/10)
            </h2>
            <p className="text-[15px] leading-relaxed text-foreground/85">
              Be extra gentle with yourself today — cancel what you can and reschedule demanding
              tasks
              {lightStart && lightEnd
                ? ` to ${formatShort(lightStart)} – ${formatShort(lightEnd)}, your next predicted low-pain stretch`
                : ''}
              . Warmth, rest, hydration, and slow breathing can take the edge off.
            </p>
            <Link
              to="/care?sos=1"
              className="inline-flex items-center gap-2 rounded-xl bg-blush-strong px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blush-strong/90"
            >
              <LifeBuoy className="size-4" aria-hidden />
              Open the instant SOS guide
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (!prediction) return null;

  const { inFlareWindow, daysUntilPeriod, nextPeriodStart, flareDates } = prediction;

  /* ---------------- Gentle mode: period timing only ---------------- */
  if (gentle) {
    const periodStarted = daysUntilPeriod < 0 && daysUntilPeriod >= -4;
    const show = periodStarted || (daysUntilPeriod >= 0 && daysUntilPeriod <= 7);
    if (!show) return null;

    const title = periodStarted
      ? 'Your predicted period is here'
      : daysUntilPeriod === 0
        ? 'Period predicted to start today'
        : daysUntilPeriod === 1
          ? 'Period predicted tomorrow'
          : `Period predicted in ${daysUntilPeriod} days`;

    const body = periodStarted
      ? `Expected around ${formatShort(nextPeriodStart)}. Take the days at your own pace — your calendar has the next few weeks mapped out for planning.`
      : `Expected around ${formatShort(nextPeriodStart)}. A good moment to restock supplies and keep plans flexible if you like to take it easy.`;

    return (
      <section
        aria-live="polite"
        className="rounded-3xl border border-aqua-strong/30 bg-gradient-to-br from-aqua via-white to-lavender p-5 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-aqua-strong/15 text-aqua-foreground">
            <CalendarHeart className="size-6" aria-hidden />
          </span>
          <div className="space-y-1.5">
            <h2 className="text-lg font-bold leading-snug text-aqua-foreground">{title}</h2>
            <p className="text-[15px] leading-relaxed text-foreground/85">{body}</p>
          </div>
        </div>
      </section>
    );
  }

  /* ---------------- Flare-up mode ---------------- */
  const daysUntilFlare = daysUntilPeriod - 1; // flare window opens the day before
  const show = inFlareWindow || (daysUntilFlare >= 0 && daysUntilFlare <= 6);
  if (!show) return null;

  const flareEnd = flareDates[flareDates.length - 1];
  const lightStart = addDaysISO(nextPeriodStart, 6);
  const lightEnd = addDaysISO(nextPeriodStart, 11);

  const title = inFlareWindow
    ? 'High-pain window is here'
    : daysUntilFlare === 0
      ? 'Flare-up likely starting today'
      : daysUntilFlare === 1
        ? 'Flare-up predicted tomorrow'
        : `Flare-up predicted in ${daysUntilFlare} days`;

  const body = inFlareWindow
    ? `Elevated pain is likely through ${formatShort(flareEnd)}. If you can, move demanding tasks to ${formatShort(lightStart)} – ${formatShort(lightEnd)}, when lighter days are expected.`
    : `Pain is predicted to peak around ${formatShort(nextPeriodStart)}. Consider rescheduling demanding tasks to ${formatShort(lightStart)} – ${formatShort(lightEnd)}, your next predicted low-pain stretch.`;

  return (
    <section
      aria-live="polite"
      className="rounded-3xl border border-blush-strong/30 bg-gradient-to-br from-blush via-white to-lavender p-5 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-blush-strong/15 text-blush-foreground">
          <CalendarClock className="size-6" aria-hidden />
        </span>
        <div className="space-y-2">
          <h2 className="text-lg font-bold leading-snug text-blush-foreground">{title}</h2>
          <p className="text-[15px] leading-relaxed text-foreground/85">{body}</p>
          {inFlareWindow && (
            <Link
              to="/care?sos=1"
              className="inline-flex items-center gap-2 rounded-xl bg-blush-strong px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blush-strong/90"
            >
              <LifeBuoy className="size-4" aria-hidden />
              Open the instant SOS guide
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
